import axios from 'axios';
import { API_KEY, ENDPOINTS } from '../config/youtube';

// Simple cache to reduce API calls
const cache = new Map();
const CACHE_DURATION = 15 * 60 * 1000; // 15 minutes (increased for quota optimization)

const getCached = (key) => {
  const cached = cache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.data;
  }
  return null;
};

const setCache = (key, data) => {
  cache.set(key, { data, timestamp: Date.now() });
};

const handleApiError = (error, context) => {
  if (error.response) {
    const status = error.response.status;
    if (status === 403) {
      console.error(`YouTube API quota exceeded or key invalid (${context})`);
      throw new Error('API_QUOTA_EXCEEDED');
    } else if (status === 404) {
      console.error(`Resource not found (${context})`);
      throw new Error('RESOURCE_NOT_FOUND');
    } else {
      console.error(`API error ${status} (${context}):`, error.response.data);
    }
  } else {
    console.error(`Network error (${context}):`, error.message);
  }
  throw error;
};

export const youtubeService = {
  // Get trending/popular videos
  async getPopularVideos(maxResults = 24, pageToken = null) {
    const cacheKey = `popular_${maxResults}_${pageToken || 'first'}`;
    const cached = getCached(cacheKey);
    if (cached) return cached;

    try {
      const params = {
        part: 'snippet,statistics,contentDetails',
        chart: 'mostPopular',
        regionCode: 'US',
        maxResults,
        key: API_KEY,
      };
      
      if (pageToken) {
        params.pageToken = pageToken;
      }
      
      const response = await axios.get(ENDPOINTS.videos, { params });
      const result = {
        items: response.data.items,
        nextPageToken: response.data.nextPageToken,
      };
      setCache(cacheKey, result);
      return result;
    } catch (error) {
      handleApiError(error, 'fetching popular videos');
      return { items: [], nextPageToken: null };
    }
  },

  // Search videos
  async searchVideos(query, maxResults = 20) {
    const cacheKey = `search_${query}_${maxResults}`;
    const cached = getCached(cacheKey);
    if (cached) return cached;

    try {
      const response = await axios.get(ENDPOINTS.search, {
        params: {
          part: 'snippet',
          q: query,
          type: 'video',
          maxResults,
          key: API_KEY,
        },
      });
      const result = response.data.items;
      setCache(cacheKey, result);
      return result;
    } catch (error) {
      handleApiError(error, 'searching videos');
      return [];
    }
  },

  // Get video details
  async getVideoDetails(videoId) {
    const cacheKey = `video_${videoId}`;
    const cached = getCached(cacheKey);
    if (cached) return cached;

    try {
      const response = await axios.get(ENDPOINTS.videos, {
        params: {
          part: 'snippet,statistics,contentDetails',
          id: videoId,
          key: API_KEY,
        },
      });
      const result = response.data.items[0];
      setCache(cacheKey, result);
      return result;
    } catch (error) {
      handleApiError(error, 'fetching video details');
      return null;
    }
  },

  // Get channel details
  async getChannelDetails(channelId) {
    try {
      const response = await axios.get(ENDPOINTS.channels, {
        params: {
          part: 'snippet,statistics',
          id: channelId,
          key: API_KEY,
        },
      });
      return response.data.items[0];
    } catch (error) {
      console.error('Error fetching channel details:', error);
      return null;
    }
  },

  // Get video comments
  async getVideoComments(videoId, maxResults = 20) {
    try {
      const response = await axios.get(ENDPOINTS.commentThreads, {
        params: {
          part: 'snippet',
          videoId: videoId,
          maxResults,
          key: API_KEY,
        },
      });
      return response.data.items;
    } catch (error) {
      console.error('Error fetching comments:', error);
      return [];
    }
  },

  // Get related videos
  async getRelatedVideos(videoId, maxResults = 12) {
    try {
      const response = await axios.get(ENDPOINTS.search, {
        params: {
          part: 'snippet',
          relatedToVideoId: videoId,
          type: 'video',
          maxResults,
          key: API_KEY,
        },
      });
      return response.data.items;
    } catch (error) {
      console.error('Error fetching related videos:', error);
      return [];
    }
  },
};
